import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';

// 1. Import your SVG logo
import logo from '../../assets/favi/aneslogo.svg'; 

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-[#0B0E14]/90 backdrop-blur-xl border-b border-white/10 shadow-2xl py-3' 
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        
        {/* 2. Updated Logo Section using your SVG */}
        <Link to="/" className="relative z-50 flex items-center group">
          <img 
            src={logo} 
            alt="ANESDEV Logo" 
            className="h-10 w-auto transition-transform duration-300 group-hover:scale-105" 
          />
        </Link>

        {/* Desktop Navigation Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            
            return (
              <Link 
                key={link.name}
                to={link.path} 
                className="relative py-2 group"
              >
                <span className={`text-sm uppercase tracking-widest transition-colors duration-300 font-medium ${
                  isActive ? 'text-white' : 'text-gray-400 group-hover:text-electric-green'
                }`}>
                  {link.name}
                </span>
                
                {isActive && (
                  <motion.div 
                    layoutId="navbar-indicator"
                    className="absolute -bottom-1 left-0 right-0 h-[2px] bg-electric-green shadow-[0_0_10px_rgba(57,255,20,0.5)] rounded-full"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}
        </div>

        {/* Premium Desktop CTA Button */}
        <Link 
          to="/contact" 
          className="hidden md:inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white/5 border border-white/10 text-white font-medium text-sm hover:bg-electric-green/10 hover:border-electric-green hover:text-electric-green transition-all duration-300 backdrop-blur-md relative z-50 group"
        >
          <span>Get a Quote</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>

        {/* Mobile Menu Toggle Button */}
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden text-white hover:text-electric-green transition-colors relative z-50"
        >
          {isOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="absolute top-full left-0 w-full bg-[#0B0E14]/95 backdrop-blur-xl border-b border-white/10 shadow-2xl md:hidden overflow-hidden"
          >
            <div className="flex flex-col px-6 py-8 gap-6">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link 
                    key={link.name} 
                    to={link.path}
                    className={`text-lg uppercase tracking-widest font-bold transition-colors ${
                      isActive ? 'text-electric-green' : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
              
              {/* Premium Mobile CTA Button */}
              <Link 
                to="/contact" 
                className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-electric-green/30 bg-electric-green/5 text-electric-green font-bold text-lg hover:bg-electric-green hover:text-[#0B0E14] transition-all duration-300 group"
              >
                <span>Get a Quote</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;